// frontend/src/ArticleDetail.js
import React, { useEffect, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { Container, Typography, Box, Chip, CircularProgress, Button } from '@mui/material';
import BackIcon from '@mui/icons-material/ArrowForward'; // RTL: "back" points right visually
import ScheduleIcon from '@mui/icons-material/Schedule';
import ElderlyIcon from '@mui/icons-material/Elderly';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import moment from 'jalali-moment';
import apiClient from './api';
import { brand, glass } from './theme';

const CATEGORY_META = {
    elderly: { label: 'مراقبت سالمند', icon: ElderlyIcon, gradient: `linear-gradient(135deg, ${brand.primary}, ${brand.primaryDeep})` },
    child: { label: 'مراقبت کودک', icon: ChildCareIcon, gradient: `linear-gradient(135deg, ${brand.accent}, ${brand.accentDeep})` },
    patient: { label: 'مراقبت بیمار', icon: LocalHospitalIcon, gradient: `linear-gradient(135deg, #6E8F87, ${brand.primaryDeep})` },
    guide: { label: 'راهنمای خانواده', icon: MenuBookIcon, gradient: `linear-gradient(135deg, ${brand.accentDeep}, #6B4A1E)` },
};

function ArticleDetail() {
    const { slug } = useParams();
    const [article, setArticle] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [loading, setLoading] = useState(true);
    const [all, setAll] = useState([]);

    useEffect(() => {
        setLoading(true);
        setNotFound(false);
        apiClient.get(`/api/articles/${slug}/`)
            .then((res) => {
                setArticle(res.data);
                document.title = `${res.data.title} | الو یارا`;
                const metaDesc = document.querySelector('meta[name="description"]');
                if (metaDesc) metaDesc.setAttribute('content', res.data.excerpt);
                let canonical = document.querySelector('link[rel="canonical"]');
                if (!canonical) {
                    canonical = document.createElement('link');
                    canonical.setAttribute('rel', 'canonical');
                    document.head.appendChild(canonical);
                }
                canonical.setAttribute('href', `https://aloyara.ir/blog/${encodeURIComponent(res.data.slug)}`);
                const ld = document.createElement('script');
                ld.type = 'application/ld+json';
                ld.id = 'article-jsonld';
                ld.text = JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'Article',
                    headline: res.data.title,
                    description: res.data.excerpt,
                    inLanguage: 'fa-IR',
                    datePublished: res.data.published_at,
                    mainEntityOfPage: `https://aloyara.ir/blog/${encodeURIComponent(res.data.slug)}`,
                    author: { '@type': 'Organization', name: 'الو یارا' },
                    publisher: { '@type': 'Organization', name: 'الو یارا', logo: { '@type': 'ImageObject', url: 'https://aloyara.ir/logo512.png' } },
                });
                document.getElementById('article-jsonld')?.remove();
                document.head.appendChild(ld);
            })
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
        return () => {
            document.getElementById('article-jsonld')?.remove();
            document.title = 'الو یارا (الویارا) | پرستار و مراقب سالمند، کودک و بیمار در منزل';
        };
    }, [slug]);

    useEffect(() => {
        apiClient.get('/api/articles/')
            .then((res) => setAll(Array.isArray(res.data) ? res.data : (res.data.results || [])))
            .catch(() => setAll([]));
    }, []);

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
    }

    if (notFound || !article) {
        return (
            <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>مقاله پیدا نشد</Typography>
                <Button component={RouterLink} to="/blog" variant="contained">بازگشت به مقالات</Button>
            </Container>
        );
    }

    const paragraphs = article.body.split(/\n\s*\n/).filter(Boolean);
    const meta = CATEGORY_META[article.category] || CATEGORY_META.guide;
    const Icon = meta.icon;
    const related = [
        ...all.filter((a) => a.slug !== article.slug && a.category === article.category),
        ...all.filter((a) => a.slug !== article.slug && a.category !== article.category),
    ].slice(0, 3);

    return (
        <Box sx={{ background: glass.canvasGradient, minHeight: '100%' }}>
            <Box sx={{ background: meta.gradient, color: brand.heroText, position: 'relative', overflow: 'hidden', pt: { xs: 5, md: 7 }, pb: { xs: 9, md: 11 } }}>
                <Box sx={{ position: 'absolute', width: 300, height: 300, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.07)', top: -140, insetInlineEnd: -80 }} />
                <Icon sx={{ position: 'absolute', fontSize: 200, opacity: 0.1, bottom: -50, insetInlineStart: -30 }} />
                <Container maxWidth="sm" sx={{ position: 'relative' }}>
                    <Button component={RouterLink} to="/blog" startIcon={<BackIcon />} size="small" sx={{ mb: 3, color: brand.heroText, opacity: 0.85 }}>
                        همه‌ی مقالات
                    </Button>
                    <Chip size="small" label={meta.label} sx={{ bgcolor: 'rgba(255,255,255,0.16)', color: brand.heroText, fontWeight: 700, mb: 2 }} />
                    <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.5 }}>{article.title}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 2, color: 'rgba(245,243,234,0.75)' }}>
                        <ScheduleIcon sx={{ fontSize: 16 }} />
                        <Typography variant="caption">{moment(article.published_at).locale('fa').format('D MMMM YYYY')} · {article.reading_minutes} دقیقه مطالعه</Typography>
                    </Box>
                </Container>
            </Box>

            <Container maxWidth="sm" sx={{ mt: -6, pb: { xs: 6, md: 9 } }}>
                <Box sx={{ ...glass.card, p: { xs: 3, md: 4.5 } }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
                        {paragraphs.map((p, i) => (
                            p.startsWith('## ') ? (
                                <Typography key={i} component="h2" sx={{ fontSize: '1.3rem', fontWeight: 800, lineHeight: 1.7, color: brand.primaryDeep, mt: 1.5 }}>
                                    {p.slice(3)}
                                </Typography>
                            ) : (
                                <Typography key={i} sx={{ fontSize: '1.05rem', lineHeight: 2, color: 'text.primary', textAlign: 'justify' }}>
                                    {p}
                                </Typography>
                            )
                        ))}
                    </Box>
                </Box>

                {related.length > 0 && (
                    <Box sx={{ mt: 4 }}>
                        <Typography component="h2" sx={{ fontWeight: 800, fontSize: '1.15rem', mb: 1.5 }}>مقالات مرتبط</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                            {related.map((a) => (
                                <Box key={a.slug} component={RouterLink} to={`/blog/${a.slug}`} sx={{ ...glass.card, p: 2, textDecoration: 'none', color: 'text.primary' }}>
                                    <Typography sx={{ fontWeight: 700, lineHeight: 1.8 }}>{a.title}</Typography>
                                    <Typography variant="body2" color="text.secondary">{a.excerpt}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}

                <Box sx={{ mt: 3, p: 3, borderRadius: '18px', bgcolor: brand.primaryTint, textAlign: 'center' }}>
                    <Typography sx={{ fontWeight: 700, mb: 1 }}>به دنبال همیار مطمئن برای خانواده‌تان هستید؟</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        در الو یارا، پروفایل، امتیاز و شرایط هر همیار را پیش از هر تصمیمی می‌بینید.
                    </Typography>
                    <Button component={RouterLink} to="/create-request" variant="contained">ثبت درخواست مراقبت</Button>
                </Box>
            </Container>
        </Box>
    );
}

export default ArticleDetail;
